import React, {PureComponent, useEffect, useState} from 'react';
import {
    PieChart,
    Pie,
    Legend,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid, Sector
} from 'recharts';
import {DashboardService} from "./Service";
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css';
import {addDays} from "date-fns"; // theme css file


const Dashboard = () => {
    const service = new DashboardService();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [cardStats, setCardStats] = useState([
        {
            name: 'Users',
            value: 0,
            icon: 'ri-user-3-line',
            symbol: ''
        },
        {
            name: 'Card Sold',
            value: 0,
            icon: 'ri-qr-code-line',
            symbol: ''
        },
        {
            name: 'Earning',
            value: 0,
            icon: 'ri-money-dollar-box-line',
            symbol: '₹'
        },
    ]);
    const [cardSalesByType, setCardSalesByType] = useState([]);
    const [cardSalesByTypeIndex, setCardSalesByTypeIndex] = useState(0);
    const [ordersByStatus, setOrdersByStatus] = useState([]);
    const [ordersByStatusIndex, setOrdersByStatusIndex] = useState(0);
    const [cardSales, setCardSales] = useState([]);
    const [cardSaleTimeFrame, setCardSaleTimeFrame] = useState('day');
    const [showCardSaleDatePicker, setShowCardSaleDatePicker] = useState(false);
    const [cardSalesDateRange, setCardSalesDateRange] = useState([
        {
            // Set start date to start of year
            startDate: new Date(new Date().getFullYear(), 0, 1),
            endDate: new Date(),
            key: 'selection'
        }
    ]);
    const [cardSalesType, setCardSalesType] = useState([]);
    const [cardSaleTypeTimeFrame, setCardSaleTypeTimeFrame] = useState('day');
    const [showCardSaleTypeDatePicker, setShowCardSaleTypeDatePicker] = useState(false);
    const [cardSalesTypeDateRange, setCardSalesTypeDateRange] = useState([
        {
            // Set start date to start of year
            startDate: new Date(new Date().getFullYear(), 0, 1),
            endDate: new Date(),
            key: 'selection'
        }
    ]);


    const data = [
        {
            name: 'Page A',
            uv: 4000,
            pv: 2400,
            amt: 2400,
        },
        {
            name: 'Page B',
            uv: 3000,
            pv: 1398,
            amt: 2210,
        },
        {
            name: 'Page C',
            uv: 2000,
            pv: 9800,
            amt: 2290,
        },
        {
            name: 'Page D',
            uv: 2780,
            pv: 3908,
            amt: 2000,
        },
        {
            name: 'Page E',
            uv: 1890,
            pv: 4800,
            amt: 2181,
        },
        {
            name: 'Page F',
            uv: 2390,
            pv: 3800,
            amt: 2500,
        },
        {
            name: 'Page G',
            uv: 3490,
            pv: 4300,
            amt: 2100,
        },
    ];

    const renderActiveShape = (props) => {
        const RADIAN = Math.PI / 180;
        const {cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value} = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';

        return (
            <g>
                <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
                    {payload.name}
                </text>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <Sector
                    cx={cx}
                    cy={cy}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    innerRadius={outerRadius + 6}
                    outerRadius={outerRadius + 10}
                    fill={fill}
                />
                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none"/>
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none"/>
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">{`${value}`}</text>
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
                    {`(${(percent * 100).toFixed(2)}%)`}
                </text>
            </g>
        );
    };

    const getCardStats = async () => {
        const res = await service.cardStats();
        if (res) {
            setCardStats([
                {
                    name: 'Users',
                    value: res['users'],
                    icon: 'ri-user-3-line',
                    symbol: ''
                },
                {
                    name: 'Card Sold',
                    value: res['cards'],
                    icon: 'ri-qr-code-line',
                    symbol: ''
                },
                {
                    name: 'Earning',
                    value: res['orders'],
                    icon: 'ri-money-dollar-box-line',
                    symbol: '₹'
                },
            ]);
        }
    }

    const getCardSalesByType = async () => {
        const res = await service.cardSalesByType({
            startDate,
            endDate
        });
        if (res) {
            setCardSalesByType(res);
        }
    }

    const getOrdersByStatus = async () => {
        const res = await service.ordersByStatus({
            startDate,
            endDate
        });
        if (res) {
            setOrdersByStatus(res);
        }
    }

    const getCardSales = async () => {
        const res = await service.cardSales({
            ...(cardSalesDateRange.length > 0 && {
                startDate: cardSalesDateRange[0]['startDate'],
                endDate: cardSalesDateRange[0]['endDate']
            }),
            timeFrame: cardSaleTimeFrame,
        });
        if (res) {
            const sales = res.map((sale) => {
                return {
                    name: sale['_id'],
                    sales: sale['totalSales'],
                }
            });
            setCardSales(sales);
        }
    }

    const getCardSalesType = async () => {
        const res = await service.cardSalesType({
            ...(cardSalesTypeDateRange.length > 0 && {
                startDate: cardSalesTypeDateRange[0]['startDate'],
                endDate: cardSalesTypeDateRange[0]['endDate']
            }),
            timeFrame: cardSaleTypeTimeFrame,
        });
        if (res) {
            const chartData = {};

            // Iterate over the response to populate the chartData object
            res.forEach(({ _id, totalSales }) => {
                const { date, type } = _id;

                if (!chartData[date]) {
                    chartData[date] = { name: date };
                }

                chartData[date][type] = totalSales;
            });

            // Convert the chartData object into an array for Recharts
            const dataForChart = Object.values(chartData);
            setCardSalesType(dataForChart);
        }
    }

    useEffect(() => {
        getCardStats();
        getCardSalesByType();
        getOrdersByStatus();
        getCardSales();
        getCardSalesType();
    }, []);

    useEffect(() => {
        getCardSales();
    }, [cardSaleTimeFrame, cardSalesDateRange]);

    useEffect(() => {
        getCardSalesType();
    }, [cardSaleTypeTimeFrame, cardSalesTypeDateRange]);

    return (
        <>
            <div className="d-flex justify-content-lg-between justify-content-sm-center flex-wrap mb-5">
                {/*  Create card to show total users  */}
                {
                    cardStats.map((card, index) => {
                        return <div className="card mb-3 flex-grow-1 m-1">
                            <div className="card-body d-flex justify-content-between">
                                <div>
                                    <h3 className="card-title text-success">{card['symbol']}{card['value']}</h3>
                                    <h5 className="card-subtitle mb-2 text-muted">{card['name']}</h5>
                                </div>
                                <div className="icon-container">
                                    <i className={card['icon']} style={{fontSize: "2rem"}}></i>
                                </div>
                            </div>

                        </div>;
                    })
                }
            </div>
            <div className="d-flex justify-content-around flex-wrap flex-row mb-5">
                <div className="card mb-3 flex-grow-1 m-1">
                    <div className="card-header p-3">
                        <h6 className="card-title">Sales By Card Type</h6>
                    </div>
                    <div className="card-body">
                        <ResponsiveContainer height={window.screen.height * 0.3}>
                            <PieChart width={window.screen.width * 0.5} height={window.screen.height * 0.3}>
                                <Pie
                                    activeIndex={cardSalesByTypeIndex}
                                    activeShape={renderActiveShape}
                                    data={cardSalesByType}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                    onMouseEnter={(data, index) => {
                                        setCardSalesByTypeIndex(index);
                                    }}
                                />

                                <Tooltip/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                </div>
                <div className="card mb-3 flex-grow-1 m-1">
                    <div className="card-header p-3">
                        <h6 className="card-title">Orders By Status</h6>
                    </div>
                    <div className="card-body">
                        <ResponsiveContainer height={window.screen.height * 0.3}>
                            <PieChart width={window.screen.width * 0.5} height={window.screen.height * 0.3}>
                                <Pie
                                    activeIndex={ordersByStatusIndex}
                                    activeShape={renderActiveShape}
                                    data={ordersByStatus}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#114232"
                                    dataKey="value"
                                    onMouseEnter={(data, index) => {
                                        setOrdersByStatusIndex(index);
                                    }}
                                />

                                <Tooltip/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                </div>

            </div>
            <div className="card mb-5" style={{width: "100%"}}>
                <div className="card-header p-3 d-flex justify-content-between align-items-center">
                    <h6 className="card-title">Card Sales</h6>
                    <div>
                        {/*  Filter to pick day|week|month|year  */}
                        <div className="btn-group">
                            <button type="button" className="btn btn-danger dropdown-toggle me-4"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                {cardSaleTimeFrame}
                            </button>
                            <ul className="dropdown-menu">
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTimeFrame('day')}>Day</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTimeFrame('week')}>Week</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTimeFrame('month')}>Month</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTimeFrame('year')}>Year</a>
                                </li>
                            </ul>
                        </div>
                        <button className="btn btn-info text-white" onClick={() => setShowCardSaleDatePicker(!showCardSaleDatePicker)}>Pick Date</button>
                    </div>
                </div>
                <div className="card-body">
                    {
                        showCardSaleDatePicker && <div className="d-flex justify-content-center mt-3 mb-3">
                            <DateRangePicker
                                onChange={item => setCardSalesDateRange([item.selection])}
                                showSelectionPreview={false}
                                moveRangeOnFirstSelection={false}
                                showDateDisplay={false}
                                showPreview={false}
                                months={1}
                                ranges={cardSalesDateRange}
                                direction="vertical"
                            />
                        </div>
                    }
                    <ResponsiveContainer height={400}>
                        <LineChart
                            width={500}
                            height={300}
                            data={cardSales}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name"/>
                            <YAxis/>
                            <Tooltip/>
                            <Legend/>
                            <Line type="monotone" dataKey="sales" stroke="#8884d8" activeDot={{r: 8}}/>
                        </LineChart>
                    </ResponsiveContainer>
                </div>

            </div>
            <div className="card mb-5" style={{width: "100%"}}>
                <div className="card-header p-3 d-flex justify-content-between align-items-center">
                    <h6 className="card-title">Card Sales By Type</h6>
                    <div>
                        {/*  Filter to pick day|week|month|year  */}
                        <div className="btn-group">
                            <button type="button" className="btn btn-danger dropdown-toggle me-4"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                {cardSaleTypeTimeFrame}
                            </button>
                            <ul className="dropdown-menu">
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTypeTimeFrame('day')}>Day</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTypeTimeFrame('week')}>Week</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTypeTimeFrame('month')}>Month</a>
                                </li>
                                <li><a className="dropdown-item" href="javascript:void(0)"
                                       onClick={() => setCardSaleTypeTimeFrame('year')}>Year</a>
                                </li>
                            </ul>
                        </div>
                        <button className="btn btn-info text-white"
                                onClick={() => setShowCardSaleTypeDatePicker(!showCardSaleTypeDatePicker)}>Pick Date
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {
                        showCardSaleTypeDatePicker && <div className="d-flex justify-content-center mt-3 mb-3">
                            <DateRangePicker
                                onChange={item => setCardSalesTypeDateRange([item.selection])}
                                showSelectionPreview={false}
                                moveRangeOnFirstSelection={false}
                                showDateDisplay={false}
                                showPreview={false}
                                months={1}
                                ranges={cardSalesTypeDateRange}
                                direction="vertical"
                            />
                        </div>
                    }
                    <ResponsiveContainer height={400}>
                        <LineChart
                            width={500}
                            height={300}
                            data={cardSalesType}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name"/>
                            <YAxis/>
                            <Tooltip/>
                            <Legend/>
                            <Line type="monotone" dataKey="Simple Card" stroke="#8884d8" activeDot={{r: 8}}/>
                            <Line type="monotone" dataKey="Google Review Card" stroke="#82ca9d" activeDot={{r: 8}}/>
                            <Line type="monotone" dataKey="Custom Card" stroke="#ff7300" activeDot={{r: 8}}/>
                        </LineChart>
                    </ResponsiveContainer>
                </div>

            </div>
        </>
    );
}

export default Dashboard;
